import React, { useState, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useAdminBooking } from "../../contexts/AdminBookingContext";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AnimatePresence, motion } from "motion/react";
import "./AdminDashboard.css";
import BrightLogo from "../../assets/BrightLogo.png";
import MyTasksPanel from "./MyTasksPanel";
import AnalyticsPanel from "./AnalyticsPanel";
import FinancialsPanel from "./FinancialsPanel";
import EmployeesPanel from "./EmployeesPanel";
import RewardsAdminPanel from "./RewardsAdminPanel";
import PinInput from "../Common/PinInput";

const ACTIVE_STATUSES = [
  "new",
  "assigned",
  "contacted",
  "awaiting_approval",
  "awaiting_payment",
  "payment_received",
];
const ALL_STATUSES = [
  ...ACTIVE_STATUSES,
  "booking_confirmed",
  "completed",
  "cancelled",
];

const BOOKING_TYPE_ICONS = {
  flight: "plane",
  hotel: "bed",
  tour: "map-marked-alt",
  visa: "passport",
  airport_transfer: "shuttle-van",
  cruise: "ship",
  insurance: "shield-alt",
  activity: "ticket-alt",
  car_rental: "car",
  custom: "concierge-bell",
};
const getBookingTypeIcon = (type) => BOOKING_TYPE_ICONS[type] || "suitcase";
const getLabel = (value) =>
  (value || "")
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const AdminDashboard = () => {
  const { userData, currentUser, logout, isAdmin, isStaff, changePassword } =
    useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // User Details Modal States

  // Add person (admin-created account) states
  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [addPersonForm, setAddPersonForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
  });
  const [addPersonLoading, setAddPersonLoading] = useState(false);

  // Self-service password (PIN) reset for the logged-in staff/admin account.
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  // Booking management states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  // Get booking management functions and REAL DATA
  const {
    allBookings,
    allUsers,
    employees,
    searchResults,
    loading: bookingLoading,
    updateBookingStatus,
    assignEmployee,
    searchUsers,
    createUser,
    getBookingStats,
  } = useAdminBooking();

  // Booking CRM detail panel (notes/tasks/quotations/assignment)

  // Calculate REAL stats from actual data
  const realStats = useMemo(() => {
    if (!allBookings || !allUsers) {
      return {
        totalUsers: 0,
        totalBookings: 0,
        totalRevenue: 0,
        activeUsers: 0,
        newUsersToday: 0,
        recentBookings: 0,
        pendingBookings: 0,
        confirmedBookings: 0,
        rejectedBookings: 0,
      };
    }

    const today = new Date();
    const todayStr = today.toDateString();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Calculate real statistics
    const totalUsers = allUsers.length;
    const totalBookings = allBookings.length;

    // Revenue is counted once a booking is actually completed.
    const totalRevenue = allBookings
      .filter(
        (booking) =>
          booking.status === "completed" &&
          (booking.finalCost || booking.estimatedCost),
      )
      .reduce(
        (sum, booking) =>
          sum + (booking.finalCost || booking.estimatedCost || 0),
        0,
      );

    // Count new users today
    const newUsersToday = allUsers.filter((user) => {
      const userDate = user.joinedAt;
      if (!userDate) return false;

      let joinDate;
      if (userDate.seconds) {
        joinDate = new Date(userDate.seconds * 1000);
      } else if (typeof userDate === "string") {
        joinDate = new Date(userDate);
      } else {
        joinDate = userDate;
      }

      return joinDate.toDateString() === todayStr;
    }).length;

    // Count recent bookings (last 7 days)
    const recentBookings = allBookings.filter((booking) => {
      const bookingDate = booking.createdAt;
      if (!bookingDate) return false;

      let createdDate;
      if (bookingDate.seconds) {
        createdDate = new Date(bookingDate.seconds * 1000);
      } else if (typeof bookingDate === "string") {
        createdDate = new Date(bookingDate);
      } else {
        createdDate = bookingDate;
      }

      return createdDate >= weekAgo;
    }).length;

    // Count active users (users with activity in last 30 days)
    const activeUsers = allUsers.filter((user) => {
      const lastActive = user.lastActive;
      if (!lastActive) return false;

      let lastActiveDate;
      if (lastActive.seconds) {
        lastActiveDate = new Date(lastActive.seconds * 1000);
      } else if (typeof lastActive === "string") {
        lastActiveDate = new Date(lastActive);
      } else {
        lastActiveDate = lastActive;
      }

      return lastActiveDate >= thirtyDaysAgo;
    }).length;

    // Booking status counts
    const pendingBookings = allBookings.filter((b) =>
      ACTIVE_STATUSES.includes(b.status),
    ).length;
    const confirmedBookings = allBookings.filter(
      (b) => b.status === "booking_confirmed" || b.status === "completed",
    ).length;
    const rejectedBookings = allBookings.filter(
      (b) => b.status === "cancelled",
    ).length;

    return {
      totalUsers,
      totalBookings,
      totalRevenue,
      activeUsers,
      newUsersToday,
      recentBookings,
      pendingBookings,
      confirmedBookings,
      rejectedBookings,
    };
  }, [allBookings, allUsers]);

  // Get real recent users (sorted by join date)
  const recentUsers = useMemo(() => {
    if (!allUsers || allUsers.length === 0) return [];

    return [...allUsers]
      .sort((a, b) => {
        const dateA = a.joinedAt?.seconds
          ? new Date(a.joinedAt.seconds * 1000)
          : new Date(a.joinedAt || 0);
        const dateB = b.joinedAt?.seconds
          ? new Date(b.joinedAt.seconds * 1000)
          : new Date(b.joinedAt || 0);
        return dateB - dateA;
      })
      .slice(0, 5)
      .map((user) => ({
        ...user,
        id: user.uid,
        displayName: user.displayName || user.name || "Unknown User",
        joinedAt: user.joinedAt?.seconds
          ? new Date(user.joinedAt.seconds * 1000)
          : new Date(user.joinedAt || Date.now()),
      }));
  }, [allUsers]);

  // Get real recent bookings (sorted by creation date)
  const recentBookings = useMemo(() => {
    if (!allBookings || allBookings.length === 0) return [];

    return [...allBookings]
      .sort((a, b) => {
        const dateA = a.createdAt?.seconds
          ? new Date(a.createdAt.seconds * 1000)
          : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.seconds
          ? new Date(b.createdAt.seconds * 1000)
          : new Date(b.createdAt || 0);
        return dateB - dateA;
      })
      .slice(0, 5)
      .map((booking) => ({
        ...booking,
        bookingDate: booking.createdAt?.seconds
          ? new Date(booking.createdAt.seconds * 1000)
          : new Date(booking.createdAt || Date.now()),
        amount: booking.finalCost || booking.estimatedCost || 0,
        destination:
          booking.destination || booking.to || booking.country || "N/A",
      }));
  }, [allBookings]);

  // User Details: now a routed page (replaces the old in-dashboard modal).
  const handleViewUserDetails = (user) => {
    navigate(`/admin/users/${user.uid}`);
  };

  const handleAddPersonSubmit = async (e) => {
    e.preventDefault();
    const { firstName, email, phone, dateOfBirth } = addPersonForm;
    if (!firstName || !email || !phone || !dateOfBirth) {
      toast.error("Please fill in name, email, phone, and date of birth");
      return;
    }

    setAddPersonLoading(true);
    const created = await createUser(addPersonForm);
    setAddPersonLoading(false);

    if (created) {
      setAddPersonForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        dateOfBirth: "",
      });
      setShowAddPersonModal(false);
    }
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    if (pwForm.current.length !== 4 || pwForm.next.length !== 4) {
      toast.error("PINs must be 4 digits");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error("New PINs do not match");
      return;
    }
    setPwSaving(true);
    try {
      await changePassword(pwForm.current, pwForm.next);
      setPwForm({ current: "", next: "", confirm: "" });
      setShowPasswordModal(false);
    } catch (_) {
      // changePassword() already surfaces a toast on failure
    } finally {
      setPwSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to logout");
    }
  };

  // User search functionality with REAL data
  const handleUserSearch = async (term) => {
    setSearchTerm(term);
    if (term.length >= 3) {
      try {
        await searchUsers(term);
      } catch (error) {
        console.error("Search error:", error);
      }
    }
  };

  // Booking action handlers with REAL functions
  const handleStatusChange = async (bookingId, status) => {
    await updateBookingStatus(bookingId, { status });
  };

  const handleAssignEmployee = async (bookingId, employeeId) => {
    await assignEmployee(bookingId, employeeId || null);
  };

  // Show loading state only if we're actually loading data
  if (bookingLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading admin dashboard...</p>
        <small>Fetching real-time data from database...</small>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-5 lg:max-w-[95%] mx-auto">
      {/* Header */}
      <header className="py-4">
        <div className="flex w-full items-center justify-between">
          <div className="brand-section">
            <div className="h-[3rem] sm:h-[5rem]">
              <img
                src={BrightLogo}
                alt="BrightLogo"
                className="h-full w-full"
              />
            </div>
            <span>Bright Wings {isAdmin ? "Admin" : "Staff"}</span>
          </div>

          <div className="nav-links">
            <button
              className={activeTab === "overview" ? "active" : ""}
              onClick={() => setActiveTab("overview")}
            >
              <i className="fas fa-tachometer-alt"></i>
              <span>Overview</span>
            </button>
            {isAdmin && (
              <button
                className={activeTab === "users" ? "active" : ""}
                onClick={() => setActiveTab("users")}
              >
                <i className="fas fa-users"></i>
                <span>Users</span>
              </button>
            )}
            {isAdmin && (
              <button
                className={activeTab === "employees" ? "active" : ""}
                onClick={() => setActiveTab("employees")}
              >
                <i className="fas fa-user-tie"></i>
                <span>Employees</span>
              </button>
            )}
            <button
              className={activeTab === "bookings" ? "active" : ""}
              onClick={() => setActiveTab("bookings")}
            >
              <i className="fas fa-calendar-check"></i>
              <span>{isAdmin ? "Bookings" : "My Leads"}</span>
            </button>
            <button
              className={activeTab === "tasks" ? "active" : ""}
              onClick={() => setActiveTab("tasks")}
            >
              <i className="fas fa-list-check"></i>
              <span>My Tasks</span>
            </button>
            {isStaff && (
              <button
                className={activeTab === "rewards" ? "active" : ""}
                onClick={() => setActiveTab("rewards")}
              >
                <i className="fas fa-gift"></i>
                <span>Rewards</span>
              </button>
            )}
            {isAdmin && (
              <button
                className={activeTab === "analytics" ? "active" : ""}
                onClick={() => setActiveTab("analytics")}
              >
                <i className="fas fa-chart-line"></i>
                <span>Analytics</span>
              </button>
            )}
            <button
              className={activeTab === "financials" ? "active" : ""}
              onClick={() => setActiveTab("financials")}
            >
              <i className="fas fa-coins"></i>
              <span>Financials</span>
            </button>
            {isAdmin && (
              <button
                className={activeTab === "search" ? "active" : ""}
                onClick={() => setActiveTab("search")}
              >
                <i className="fas fa-search"></i>
                <span>Search Users</span>
              </button>
            )}
          </div>

          <div className="admin-user-menu">
            <button
              onClick={() => navigate("/dashboard")}
              className="user-view-btn"
            >
              <i className="fas fa-user"></i>
              <span>User View</span>
            </button>
            <div className="admin-user-info">
              <span className="admin-user-name">
                {userData?.displayName || "Admin"}
              </span>
              <div className="admin-user-avatar">
                {userData?.photoURL ? (
                  <img src={userData.photoURL} alt="Admin" />
                ) : (
                  <i className="fas fa-user-shield"></i>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="admin-logout-btn"
              title="Change password"
            >
              <i className="fas fa-key"></i>
            </button>
            <button onClick={handleLogout} className="admin-logout-btn">
              <i className="fas fa-sign-out-alt"></i>
            </button>

            <div className=" lg:hidden ">
              <button onClick={() => setIsMenuOpen(true)}>
                <i className="fas fa-bars fa-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="absolute top-0 transition-transform duration-300 bg-white p-5 text-lg flex flex-col gap-3 items-start h-full w-full z-[99999]"
          >
            <div className="w-full flex justify-end px-7">
              <button onClick={() => setIsMenuOpen(false)}>
                <i className="fas fa-close fa-xl"></i>
              </button>
            </div>

            <button
              className={`flex gap-2 hover:bg-slate-200 w-full p-1 items-center rounded-md cursor-pointer  ${
                activeTab === "overview" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("overview");
                setIsMenuOpen(false);
              }}
            >
              <i className="fas fa-tachometer-alt"></i>
              <span>Overview</span>
            </button>

            {isAdmin && (
              <button
                className={`flex gap-2 hover:bg-slate-200 w-full p-1 items-center rounded-md cursor-pointer  ${
                  activeTab === "users" ? "active" : ""
                }`}
                onClick={() => {
                  setActiveTab("users");
                  setIsMenuOpen(false);
                }}
              >
                <i className="fas fa-users"></i>
                <span>Users</span>
              </button>
            )}
            {isAdmin && (
              <button
                className={`flex gap-2 hover:bg-slate-200 w-full p-1 items-center rounded-md cursor-pointer  ${
                  activeTab === "employees" ? "active" : ""
                }`}
                onClick={() => {
                  setActiveTab("employees");
                  setIsMenuOpen(false);
                }}
              >
                <i className="fas fa-user-tie"></i>
                <span>Employees</span>
              </button>
            )}
            <button
              className={`flex gap-2 hover:bg-slate-200 w-full p-1 items-center rounded-md cursor-pointer  ${
                activeTab === "bookings" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("bookings");
                setIsMenuOpen(false);
              }}
            >
              <i className="fas fa-calendar-check"></i>
              <span>{isAdmin ? "Bookings" : "My Leads"}</span>
            </button>
            <button
              className={`flex gap-2 hover:bg-slate-200 w-full p-1 items-center rounded-md cursor-pointer  ${
                activeTab === "tasks" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("tasks");
                setIsMenuOpen(false);
              }}
            >
              <i className="fas fa-list-check"></i>
              <span>My Tasks</span>
            </button>
            {isStaff && (
              <button
                className={`flex gap-2 hover:bg-slate-200 w-full p-1 items-center rounded-md cursor-pointer  ${
                  activeTab === "rewards" ? "active" : ""
                }`}
                onClick={() => {
                  setActiveTab("rewards");
                  setIsMenuOpen(false);
                }}
              >
                <i className="fas fa-gift"></i>
                <span>Rewards</span>
              </button>
            )}
            {isAdmin && (
              <button
                className={`flex gap-2 hover:bg-slate-200 w-full p-1 items-center rounded-md cursor-pointer  ${
                  activeTab === "analytics" ? "active" : ""
                }`}
                onClick={() => {
                  setActiveTab("analytics");
                  setIsMenuOpen(false);
                }}
              >
                <i className="fas fa-chart-line"></i>
                <span>Analytics</span>
              </button>
            )}
            <button
              className={`flex gap-2 hover:bg-slate-200 w-full p-1 items-center rounded-md cursor-pointer  ${
                activeTab === "financials" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("financials");
                setIsMenuOpen(false);
              }}
            >
              <i className="fas fa-coins"></i>
              <span>Financials</span>
            </button>
            {isAdmin && (
              <button
                className={`flex gap-2 hover:bg-slate-200 w-full p-1 items-center rounded-md cursor-pointer  ${
                  activeTab === "search" ? "active" : ""
                }`}
                onClick={() => {
                  setActiveTab("search");
                  setIsMenuOpen(false);
                }}
              >
                <i className="fas fa-search"></i>
                <span>Search Users</span>
              </button>
            )}
            <button
              className="flex gap-2 hover:bg-slate-200 w-full p-1 items-center rounded-md cursor-pointer"
              onClick={() => {
                setShowPasswordModal(true);
                setIsMenuOpen(false);
              }}
            >
              <i className="fas fa-key"></i>
              <span>Change Password</span>
            </button>
            <button
              className="flex gap-2 hover:bg-slate-200 w-full p-1 items-center rounded-md cursor-pointer"
              onClick={() => {
                handleLogout();
                setIsMenuOpen(false);
              }}
            >
              <i className="fas fa-sign-out-alt"></i>
              <span>Log Out</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="admin-main p-2">
        {activeTab === "overview" && (
          <div className="overview-content">
            <div
              className="admin-welcome"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: "1rem",
              }}
            >
              <div>
                <h1>{isAdmin ? "Admin Dashboard" : "Staff Dashboard"}</h1>
                <p>Real-time overview of your travel portal performance</p>
                <small style={{ color: "#6b7280" }}>
                  Last updated: {new Date().toLocaleTimeString()}
                </small>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => setShowAddPersonModal(true)}
              >
                <i className="fas fa-user-plus"></i> Add Customer
              </button>
            </div>

            {/* REAL Stats Cards with INR */}
            <div className="admin-stats-grid">
              <div className="admin-stat-card users">
                <div className="stat-icon">
                  <i className="fas fa-users"></i>
                </div>
                <div className="stat-details">
                  <h3>{realStats.totalUsers.toLocaleString()}</h3>
                  <p>Total Users</p>
                  <span className="stat-change">
                    +{realStats.newUsersToday} today
                  </span>
                </div>
              </div>

              <div className="admin-stat-card bookings">
                <div className="stat-icon">
                  <i className="fas fa-calendar-check"></i>
                </div>
                <div className="stat-details">
                  <h3>{realStats.totalBookings.toLocaleString()}</h3>
                  <p>Total Bookings</p>
                  <span className="stat-change">
                    +{realStats.recentBookings} this week
                  </span>
                </div>
              </div>

              <div className="admin-stat-card revenue">
                <div className="stat-icon">
                  <i className="fas fa-rupee-sign"></i>
                </div>
                <div className="stat-details">
                  <h3>{formatCurrency(realStats.totalRevenue)}</h3>
                  <p>Total Revenue</p>
                  <span className="stat-change">
                    From {realStats.confirmedBookings} confirmed bookings
                  </span>
                </div>
              </div>

              <div className="admin-stat-card active">
                <div className="stat-icon">
                  <i className="fas fa-user-check"></i>
                </div>
                <div className="stat-details">
                  <h3>{realStats.activeUsers}</h3>
                  <p>Active Users</p>
                  <span className="stat-change">Last 30 days</span>
                </div>
              </div>
            </div>

            {/* Dashboard Grid with REAL DATA */}
            <div className="admin-dashboard-grid">
              {/* Recent Users - REAL DATA */}
              <div className="admin-card">
                <div className="w-full flex items-center justify-between p-3">
                  <h2 className="text-xl">Recent Users</h2>
                  <div>
                    <button
                      className="view-all-btn"
                      onClick={() => setActiveTab("users")}
                    >
                      View All <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>

                <div className="p-2">
                  <div className="flex flex-col gap-3">
                    {recentUsers.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "2rem",
                          color: "#6b7280",
                        }}
                      >
                        <i
                          className="fas fa-users"
                          style={{
                            fontSize: "2rem",
                            marginBottom: "1rem",
                            opacity: 0.5,
                          }}
                        ></i>
                        <p>No users found</p>
                      </div>
                    ) : (
                      recentUsers.map((user) => (
                        <div key={user.id} className="user-item">
                          <div className="user-avatar">
                            {user.photoURL ? (
                              <img src={user.photoURL} alt={user.displayName} />
                            ) : (
                              <i className="fas fa-user"></i>
                            )}
                          </div>
                          <div className="user-details">
                            <h4>{user.displayName}</h4>
                            <p>{user.email}</p>
                            <span className="join-date">
                              Joined {user.joinedAt.toLocaleDateString()}
                            </span>
                          </div>
                          <div className="user-stats">
                            <span
                              className={`membership-badge ${
                                user.membershipTier?.toLowerCase() || "bronze"
                              }`}
                            >
                              {user.membershipTier || "Bronze"}
                            </span>
                            <span className="user-bookings">
                              {user.totalBookings || 0} bookings
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Bookings - REAL DATA with INR */}
              <div className="admin-card">
                <div className="flex items-center justify-between py-2 px-4">
                  <h2 className="text-xl">Recent Bookings</h2>
                  <div>
                    <button
                      className="view-all-btn"
                      onClick={() => setActiveTab("bookings")}
                    >
                      View All <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>

                <div className="p-3">
                  <div className="flex flex-col gap-3">
                    {recentBookings.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "2rem",
                          color: "#6b7280",
                        }}
                      >
                        <i
                          className="fas fa-calendar-check"
                          style={{
                            fontSize: "2rem",
                            marginBottom: "1rem",
                            opacity: 0.5,
                          }}
                        ></i>
                        <p>No recent bookings found</p>
                      </div>
                    ) : (
                      recentBookings.map((booking) => (
                        <div key={booking.id} className="booking-item">
                          <div className="booking-icon">
                            <i
                              className={`fas fa-${getBookingTypeIcon(
                                booking.type,
                              )}`}
                            ></i>
                          </div>
                          <div className="booking-details">
                            <h4>{booking.destination}</h4>
                            <p>{booking.userEmail}</p>
                            <span className="booking-date">
                              {booking.bookingDate.toLocaleDateString()}
                            </span>
                          </div>
                          <div className="booking-status">
                            <span className={`status ${booking.status}`}>
                              {getLabel(booking.status)}
                            </span>
                            <span className="booking-amount">
                              {formatCurrency(booking.amount)}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "users" && isAdmin && (
          <div className="users-content">
            <div className="section-header flex items-center justify-between">
              <div>
                <h1>User Management</h1>
                <p>Manage user accounts and permissions</p>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => setShowAddPersonModal(true)}
              >
                <i className="fas fa-user-plus"></i> Add Person
              </button>
            </div>

            <div className=" overflow-x-auto">
              <table className="users-table w-full border-collapse">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left">User</th>
                    <th className="px-4 py-2 text-left">Email</th>
                    <th className="px-4 py-2 text-left">Membership</th>
                    <th className="px-4 py-2 text-left">Wings</th>
                    <th className="px-4 py-2 text-left">Total Spent</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(allUsers || []).slice(0, 10).map((user) => (
                    <tr key={user.uid} className="border-b">
                      {/* User */}
                      <td className="px-4 py-2 user-cell">
                        <div className="user-info flex items-center gap-3">
                          <div className="user-avatar-small w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-gray-200">
                            {user.photoURL ? (
                              <img
                                src={user.photoURL}
                                alt={user.displayName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <i className="fas fa-user text-gray-500"></i>
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium">
                              {user.displayName || user.name || "Unknown User"}
                            </h4>
                            <span className="text-sm text-gray-500">
                              Joined{" "}
                              {user.joinedAt
                                ? user.joinedAt.seconds
                                  ? new Date(
                                      user.joinedAt.seconds * 1000,
                                    ).toLocaleDateString()
                                  : new Date(user.joinedAt).toLocaleDateString()
                                : "Unknown"}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Email */}
                      <td className="px-4 py-2">{user.email}</td>

                      {/* Membership */}
                      <td className="px-4 py-2">
                        <span
                          className={`membership-badge ${
                            user.membershipTier?.toLowerCase() || "bronze"
                          }`}
                        >
                          {user.membershipTier || "Bronze"}
                        </span>
                      </td>

                      {/* Tokens */}
                      <td className="px-4 py-2">
                        <span className="token-count">{user.tokens || 0}</span>
                      </td>

                      {/* Total Spent */}
                      <td className="px-4 py-2">
                        <span className="spent-amount">
                          {formatCurrency(user.totalSpent || 0)}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-2">
                        <span className="status-badge active">
                          {getLabel(user.role) || "Customer"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleViewUserDetails(user)}
                        >
                          <i className="fas fa-eye"></i> View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "bookings" && (
          <div className="p-2">
            <div className="section-header">
              <h1>{isAdmin ? "Booking Management" : "My Assigned Leads"}</h1>
              <p>
                {isAdmin
                  ? "Manage all travel bookings and requests"
                  : "Bookings assigned to you"}
              </p>
            </div>

            {/* REAL Booking Statistics with INR */}
            <div className="mb-10">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 ">
                <div className="stat-item">
                  <span className="stat-number">{realStats.totalBookings}</span>
                  <span className="stat-label">Total Bookings</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">
                    {realStats.pendingBookings}
                  </span>
                  <span className="stat-label">In Progress</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">
                    {realStats.confirmedBookings}
                  </span>
                  <span className="stat-label">Confirmed/Completed</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">
                    {formatCurrency(realStats.totalRevenue)}
                  </span>
                  <span className="stat-label">Total Revenue</span>
                </div>
              </div>
            </div>

            <div className="rounded-md w-full overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left">Booking ID</th>
                    <th className="px-4 py-2 text-left">Customer</th>
                    <th className="px-4 py-2 text-left">Type</th>
                    <th className="px-4 py-2 text-left">Details</th>
                    <th className="px-4 py-2 text-left">Cost (INR)</th>
                    <th className="px-4 py-2 text-left">Date</th>
                    {isAdmin && (
                      <th className="px-4 py-2 text-left">Assigned To</th>
                    )}
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(allBookings || []).map((booking) => (
                    <tr key={booking.id} className="border-b">
                      {/* Booking ID */}
                      <td className="px-4 py-2">
                        <span className="booking-id">#{booking.id}</span>
                      </td>

                      {/* Customer */}
                      <td className="px-4 py-2">
                        <div className="customer-info">
                          <strong>
                            {booking.userName ||
                              booking.userEmail?.split("@")[0] ||
                              "Unknown"}
                          </strong>
                          <br />
                          <small>{booking.userEmail}</small>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-4 py-2">
                        <span className={`type-badge ${booking.type}`}>
                          <i
                            className={`fas fa-${getBookingTypeIcon(
                              booking.type,
                            )}`}
                          ></i>{" "}
                          {getLabel(booking.type)}
                        </span>
                      </td>

                      {/* Details */}
                      <td className="px-4 py-2">
                        <span>
                          {booking.from && booking.to
                            ? `${booking.from} → ${booking.to}`
                            : booking.destination || booking.country || "—"}
                        </span>
                      </td>

                      {/* Cost */}
                      <td className="px-4 py-2">
                        <div className="cost-info">
                          <span className="estimated">
                            Est: {formatCurrency(booking.estimatedCost || 0)}
                          </span>
                          {booking.finalCost > 0 && (
                            <span className="final block">
                              Final: {formatCurrency(booking.finalCost)}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Date */}
                      <td className="px-4 py-2">
                        {booking.createdAt
                          ? booking.createdAt.seconds
                            ? new Date(
                                booking.createdAt.seconds * 1000,
                              ).toLocaleDateString()
                            : new Date(booking.createdAt).toLocaleDateString()
                          : "N/A"}
                      </td>

                      {/* Assigned To (admin only) */}
                      {isAdmin && (
                        <td className="px-4 py-2">
                          <select
                            className="form-control"
                            value={booking.assignedEmployeeId || ""}
                            onChange={(e) =>
                              handleAssignEmployee(booking.id, e.target.value)
                            }
                          >
                            <option value="">Unassigned</option>
                            {employees.map((emp) => (
                              <option key={emp.uid} value={emp.uid}>
                                {emp.displayName}
                              </option>
                            ))}
                          </select>
                        </td>
                      )}

                      {/* Status */}
                      <td className="px-4 py-2">
                        <select
                          className={`status ${booking.status}`}
                          value={booking.status}
                          onChange={(e) =>
                            handleStatusChange(booking.id, e.target.value)
                          }
                        >
                          {ALL_STATUSES.map((status) => (
                            <option key={status} value={status}>
                              {getLabel(status)}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-2">
                        <div className="action-buttons flex gap-2">
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => navigate(`/admin/bookings/${booking.docId}`)}
                          >
                            <i className="fas fa-clipboard-list"></i> Manage
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "tasks" && <MyTasksPanel />}

        {activeTab === "analytics" && isAdmin && <AnalyticsPanel />}

        {activeTab === "financials" && (
          <div className="p-4">
            <h2 className="mb-4 text-lg font-bold text-gray-800">
              Financials &amp; PnL
            </h2>
            <FinancialsPanel />
          </div>
        )}

        {activeTab === "employees" && isAdmin && <EmployeesPanel />}

        {activeTab === "rewards" && isStaff && <RewardsAdminPanel />}

        {activeTab === "search" && isAdmin && (
          <div className="search-content">
            <div className="section-header">
              <h1>User Search</h1>
              <p>Search users by membership card, phone, or email</p>
            </div>

            <div className="search-container">
              <div className="search-box">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleUserSearch(e.target.value)}
                  placeholder="Search by card number (BW-XXX-XXXX), phone, email, or name..."
                  className="user-search-input"
                />
                <i className="fas fa-search search-icon"></i>
              </div>

              {searchResults.length > 0 && (
                <div className="search-results">
                  <h3>Search Results ({searchResults.length})</h3>

                  {searchResults.map((user) => (
                    <div key={user.uid} className="user-result-card">
                      <div className="user-basic-info">
                        <div className="user-avatar-result">
                          {user.photoURL ? (
                            <img src={user.photoURL} alt={user.displayName} />
                          ) : (
                            <i className="fas fa-user"></i>
                          )}
                        </div>
                        <div className="user-details-result">
                          <h4>
                            {user.displayName || user.name || "Unknown User"}
                          </h4>
                          <p>{user.email}</p>
                          <span className="membership-info">
                            <strong>{user.membershipCode || "N/A"}</strong> •{" "}
                            {user.membershipTier || "Bronze"} Member
                          </span>
                          <span className="contact-info">
                            <i className="fas fa-credit-card"></i>{" "}
                            {user.cardNumber || "No card number"}
                          </span>
                          <span className="contact-info">
                            <i className="fas fa-phone"></i>{" "}
                            {user.profile?.phone || "No phone"}
                          </span>
                        </div>
                      </div>

                      <div className="user-stats-result">
                        <div className="stat-item-small">
                          <span className="stat-number">
                            {user.tokens || 0}
                          </span>
                          <span className="stat-label">Wings</span>
                        </div>
                        <div className="stat-item-small">
                          <span className="stat-number">
                            {user.totalBookings || 0}
                          </span>
                          <span className="stat-label">Bookings</span>
                        </div>
                        <div className="stat-item-small">
                          <span className="stat-number">
                            {formatCurrency(user.totalSpent || 0)}
                          </span>
                          <span className="stat-label">Spent</span>
                        </div>
                      </div>

                      <div className="user-actions-result">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleViewUserDetails(user)}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {searchTerm.length > 0 && searchResults.length === 0 && (
                <div className="no-results">
                  <i className="fas fa-search"></i>
                  <p>No users found matching "{searchTerm}"</p>
                  <span>
                    Try searching by membership code, card number, phone number,
                    email, or name
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </main>


      {/* Add Person Modal */}
      {showAddPersonModal && (
        <div className="token-modal">
          <div
            className="modal-overlay"
            onClick={() => setShowAddPersonModal(false)}
          ></div>
          <div className="token-modal-content">
            <div className="modal-header">
              <h3>Add Person</h3>
              <button
                className="close-btn"
                onClick={() => setShowAddPersonModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form className="token-modal-body" onSubmit={handleAddPersonSubmit}>
              <p style={{ color: "#6b7280", marginBottom: "0.5rem" }}>
                Their login PIN is set to the last 4 digits of their phone
                number. They can change it later from their profile.
              </p>
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={addPersonForm.firstName}
                  onChange={(e) =>
                    setAddPersonForm((f) => ({
                      ...f,
                      firstName: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={addPersonForm.lastName}
                  onChange={(e) =>
                    setAddPersonForm((f) => ({
                      ...f,
                      lastName: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  className="form-control"
                  value={addPersonForm.email}
                  onChange={(e) =>
                    setAddPersonForm((f) => ({ ...f, email: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone (last 4 digits become their PIN) *</label>
                <input
                  type="tel"
                  className="form-control"
                  value={addPersonForm.phone}
                  onChange={(e) =>
                    setAddPersonForm((f) => ({ ...f, phone: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Date of Birth *</label>
                <input
                  type="date"
                  className="form-control"
                  value={addPersonForm.dateOfBirth}
                  onChange={(e) =>
                    setAddPersonForm((f) => ({
                      ...f,
                      dateOfBirth: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="token-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowAddPersonModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={addPersonLoading}
                >
                  <i className="fas fa-user-plus"></i>
                  {addPersonLoading ? "Creating..." : "Create Account"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password (PIN) Modal -- staff/admin self-service reset */}
      {showPasswordModal && (
        <div className="token-modal">
          <div
            className="modal-overlay"
            onClick={() => !pwSaving && setShowPasswordModal(false)}
          ></div>
          <div className="token-modal-content">
            <div className="modal-header">
              <h3>Change Password</h3>
              <button
                className="close-btn"
                onClick={() => setShowPasswordModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form
              className="token-modal-body"
              onSubmit={handleChangePasswordSubmit}
            >
              <p style={{ color: "#6b7280", marginBottom: "0.5rem" }}>
                Enter your current PIN, then your new PIN twice to confirm.
              </p>
              <div className="form-group">
                <label>Current PIN</label>
                <PinInput
                  value={pwForm.current}
                  onChange={(v) => setPwForm((f) => ({ ...f, current: v }))}
                  ariaLabel="Current PIN"
                />
              </div>
              <div className="form-group">
                <label>New PIN</label>
                <PinInput
                  value={pwForm.next}
                  onChange={(v) => setPwForm((f) => ({ ...f, next: v }))}
                  ariaLabel="New PIN"
                />
              </div>
              <div className="form-group">
                <label>Confirm New PIN</label>
                <PinInput
                  value={pwForm.confirm}
                  onChange={(v) => setPwForm((f) => ({ ...f, confirm: v }))}
                  ariaLabel="Confirm New PIN"
                />
              </div>
              <div className="token-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={pwSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={pwSaving}
                >
                  <i className="fas fa-key"></i>
                  {pwSaving ? "Saving..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;
